import React, { useState } from 'react';
import { Eye, EyeOff, Wand2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export const PASSWORD_RULES = [
  { label: '8+ karaktere', test: (value) => value.length >= 8 },
  { label: 'shkronjë e madhe', test: (value) => /[A-Z]/.test(value) },
  { label: 'shkronjë e vogël', test: (value) => /[a-z]/.test(value) },
  { label: 'numër', test: (value) => /\d/.test(value) },
  { label: 'karakter special', test: (value) => /[^A-Za-z0-9]/.test(value) },
];

export const passwordMeetsRules = (value) => PASSWORD_RULES.every((rule) => rule.test(value));

const STRENGTH_LEVELS = [
  { label: 'E dobët', text: 'text-[#DD0000]', bar: 'bg-[#DD0000]' },
  { label: 'Mesatare', text: 'text-[#B38F00]', bar: 'bg-[#FFCE00]' },
  { label: 'E fortë', text: 'text-emerald-600', bar: 'bg-emerald-600' },
];

// 0 = weak, 1 = medium, 2 = strong
const strengthLevel = (value) => {
  const passed = PASSWORD_RULES.filter((rule) => rule.test(value)).length;
  if (passed === PASSWORD_RULES.length) return 2;
  if (passed >= 3) return 1;
  return 0;
};

export const generateSecurePassword = () => {
  const characterSets = [
    'ABCDEFGHJKLMNPQRSTUVWXYZ',
    'abcdefghijkmnopqrstuvwxyz',
    '23456789',
    '!@#$%&*+-=?',
  ];
  const allCharacters = characterSets.join('');
  const secureIndex = (length) => {
    const randomValues = new Uint8Array(1);
    const maxValidValue = 256 - (256 % length);
    do {
      window.crypto.getRandomValues(randomValues);
    } while (randomValues[0] >= maxValidValue);
    return randomValues[0] % length;
  };

  if (!window.crypto?.getRandomValues) {
    throw new Error('Secure random number generation is unavailable.');
  }
  const characters = characterSets.map((set) => set[secureIndex(set.length)]);
  while (characters.length < 16) {
    characters.push(allCharacters[secureIndex(allCharacters.length)]);
  }
  for (let index = characters.length - 1; index > 0; index -= 1) {
    const swapIndex = secureIndex(index + 1);
    [characters[index], characters[swapIndex]] = [characters[swapIndex], characters[index]];
  }
  return characters.join('');
};

/**
 * Password field with show/hide toggle, optional generator button
 * and an optional 3-step strength meter with the missing requirements.
 */
const PasswordInput = ({
  id,
  name,
  value,
  onChange,
  onGenerate,
  showStrength = false,
  invalid = false,
  placeholder,
  autoComplete,
  required,
  visible,
  onVisibleChange,
}) => {
  const [localVisible, setLocalVisible] = useState(false);
  const isVisible = visible ?? localVisible;
  const setVisible = onVisibleChange ?? setLocalVisible;
  const level = strengthLevel(value);
  const strength = STRENGTH_LEVELS[level];
  const missing = PASSWORD_RULES.filter((rule) => !rule.test(value)).map((rule) => rule.label);

  return (
    <div className="space-y-2.5">
      <div className="relative">
        <input
          id={id}
          name={name}
          type={isVisible ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          aria-invalid={invalid || undefined}
          className={cn(
            'h-11 w-full rounded-xl border bg-white/75 dark:bg-zinc-800/60 backdrop-blur-xs pl-3.5 text-sm text-zinc-900 dark:text-zinc-100 shadow-xs transition placeholder:text-zinc-400 focus:outline-none focus:ring-4 focus:bg-white dark:focus:bg-zinc-800',
            onGenerate ? 'pr-[4.75rem]' : 'pr-11',
            invalid
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-zinc-200 hover:border-zinc-300 focus:border-[#DD0000]/50 focus:ring-[#DD0000]/10',
          )}
        />
        <div className="absolute inset-y-0 right-1.5 flex items-center gap-0.5">
          {onGenerate && (
            <button
              type="button"
              onClick={() => {
                onGenerate();
                setVisible(true);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 transition hover:bg-zinc-100 hover:text-[#DD0000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DD0000]/30"
              aria-label="Gjenero fjalëkalim të sigurt"
              title="Gjenero fjalëkalim të sigurt"
            >
              <Wand2 size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setVisible(!isVisible)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DD0000]/30"
            aria-label={isVisible ? 'Fshih fjalëkalimin' : 'Shfaq fjalëkalimin'}
          >
            {isVisible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      {showStrength && value && (
        <div className="space-y-1.5" aria-live="polite">
          <div className="flex items-center gap-2">
            <div className="grid flex-1 grid-cols-3 gap-1.5" aria-hidden="true">
              {STRENGTH_LEVELS.map((step, index) => (
                <span
                  key={step.label}
                  className={`h-1.5 rounded-full transition-colors ${index <= level ? strength.bar : 'bg-zinc-200'}`}
                />
              ))}
            </div>
            <span className={`w-16 shrink-0 text-right text-xs font-semibold ${strength.text}`}>{strength.label}</span>
          </div>
          {missing.length > 0 && (
            <p className="text-xs text-zinc-500">Ende nevojitet: {missing.join(', ')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
